import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Mail, AlertTriangle } from "lucide-react";

/**
 * Slide-in form that lets a coach file a league-notice with the Club Secretary
 * when they cancel or amend a game booking. Everything except the reason and
 * opposition club secretary contact details is pre-filled from the booking so
 * the coach only ever types what the system genuinely can't infer.
 */
export function SecretaryNoticeDialog({ open, booking, kind, onClose, onSent }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !booking) return;
    const orig = `${String(booking.start_hour).padStart(2, "0")}:${String(booking.start_min || 0).padStart(2, "0")}`;
    setForm({
      league: (typeof booking.league === "object" ? booking.league?.name : booking.league) || "",
      division: booking.division || "",
      opponent: booking.opponent || "",
      opp_secretary_name: "",
      opp_secretary_email: "",
      date_of_fixture: booking.booking_date || "",
      original_kickoff: orig,
      new_kickoff: "",
      reason: "",
    });
  }, [open, booking]);

  if (!open || !form || !booking) return null;

  const isAmend = kind === "amend";
  const canSubmit = form.opp_secretary_name.trim() && form.opp_secretary_email.trim() && form.reason.trim() && (!isAmend || form.new_kickoff);

  const submit = async () => {
    if (!canSubmit) { toast.error("Opposition secretary contact + reason are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/secretary-notice`, {
        kind,
        league: form.league || null,
        division: form.division || null,
        opponent: form.opponent || null,
        opp_secretary_name: form.opp_secretary_name.trim(),
        opp_secretary_email: form.opp_secretary_email.trim(),
        date_of_fixture: form.date_of_fixture || null,
        original_kickoff: form.original_kickoff || null,
        new_kickoff: isAmend ? form.new_kickoff : null,
        reason: form.reason.trim(),
      });
      if (data.cadence === "realtime") {
        toast.success(data.email_sent
          ? "Notice filed + club secretary emailed"
          : `Notice filed. Email failed: ${data.email_error || "unknown error"}`);
      } else {
        toast.success(`Notice queued for the ${data.cadence} digest`);
      }
      onSent?.(data);
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to file notice");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="secretary-notice-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-2xl flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#83B68B]" /> Notify Club Secretary — {isAmend ? "amendment" : "cancellation"}
          </DialogTitle>
          <DialogDescription className="text-xs opacity-70">
            The Club Secretary uses these details to pass the change on to the opposition. Fields we already know are pre-filled.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="rounded-md border border-[#83B68B]/30 bg-[#83B68B]/[0.04] p-3 text-xs">
            <span className="opacity-70">EYFC team:</span> <strong>{booking.team_name || booking.age_group || "—"}</strong>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">League</Label>
              <Input value={form.league} onChange={e => setForm({ ...form, league: e.target.value })} placeholder="e.g. Herts League" data-testid="notice-league" />
            </div>
            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Division</Label>
              <Input value={form.division} onChange={e => setForm({ ...form, division: e.target.value })} placeholder="e.g. Div 2" data-testid="notice-division" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Opposition</Label>
              <Input value={form.opponent} onChange={e => setForm({ ...form, opponent: e.target.value })} placeholder="Opposition team name" data-testid="notice-opponent" />
            </div>
            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Opp. Club Secretary <span className="text-[#83B68B]">*</span></Label>
              <Input value={form.opp_secretary_name} onChange={e => setForm({ ...form, opp_secretary_name: e.target.value })} placeholder="Full name" data-testid="notice-opp-name" />
            </div>
            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Opp. Secretary email <span className="text-[#83B68B]">*</span></Label>
              <Input type="email" value={form.opp_secretary_email} onChange={e => setForm({ ...form, opp_secretary_email: e.target.value })} placeholder="secretary@theirclub.co.uk" data-testid="notice-opp-email" />
            </div>
            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Date of fixture</Label>
              <Input type="date" value={form.date_of_fixture} onChange={e => setForm({ ...form, date_of_fixture: e.target.value })} data-testid="notice-date" />
            </div>
            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Original kick-off</Label>
              <Input value={form.original_kickoff} onChange={e => setForm({ ...form, original_kickoff: e.target.value })} placeholder="HH:MM" data-testid="notice-orig-kickoff" />
            </div>
            {isAmend && (
              <div className="sm:col-span-2">
                <Label className="text-[10px] tracking-widest uppercase opacity-70">New kick-off <span className="text-[#83B68B]">*</span></Label>
                <Input value={form.new_kickoff} onChange={e => setForm({ ...form, new_kickoff: e.target.value })} placeholder="HH:MM · new date if changed" data-testid="notice-new-kickoff" />
              </div>
            )}
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Reason <span className="text-[#83B68B]">*</span></Label>
            <Textarea rows={3} value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Weather · availability · pitch clash · double booking …" data-testid="notice-reason" />
          </div>
          <div className="rounded-md border border-amber-400/40 bg-amber-500/[0.04] p-3 text-[11px] flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
            <span className="opacity-80">The secretary receives this by email; batched digests are sent per your club's cadence setting.</span>
          </div>
          <div className="flex gap-2 pt-1 sticky bottom-0 bg-background pb-1">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-md border border-border text-xs tracking-widest uppercase font-display font-bold hover:border-white/40" data-testid="notice-cancel-btn">Not now</button>
            <button type="button" onClick={submit} disabled={!canSubmit || busy} data-testid="notice-submit-btn" className="btn-volt flex-1 py-3 rounded-md disabled:opacity-40 inline-flex items-center justify-center gap-2">
              <Mail className="w-4 h-4" /> {busy ? "Filing…" : "File notice"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
